import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import NcImage from "shared/NcImage/NcImage";

/**
 * Restyled copy of containers/Messages/MessageList.
 * Data handling is identical: same visibility rules (event_for), same grouping by date,
 * same paging. Only markup and styling changed.
 */

interface Sender { id?: number; name?: string; image?: string | null }
interface Message {
  id: number;
  body: { name?: string; url?: string | null };
  event_message: string | null;
  event_for: string;
  sender_id: number;
  sender: Sender;
  attachments: any[];
  created_at: string;
}
interface MessageGroup { date: string; messages: Message[] }
interface ApiResponse {
  message: MessageGroup[];
  meta?: { next_page: number | null };
}

interface Props {
  modifiedBookingMap: ApiResponse[] | ApiResponse;
  showMore: boolean; // true while loading
  loadMoreMessages: (page: number) => Promise<void>;
  /** Rendered under the newest "invoice" system message (the live invoice card). */
  invoiceCard?: React.ReactNode;
}

/** System notice about an invoice being sent/received (not deleted/canceled ones). */
const isInvoiceNotice = (m: Message) => {
  const ev = m.event_message || "";
  const text = m.body?.name || "";
  const mentions = /invoice/i.test(ev) || /received an invoice|invoice (was )?sent|sent (you )?an invoice/i.test(text);
  return mentions && !/delet|cancel|remov|expire|withdr/i.test(ev + " " + text);
};

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const NOTICE: Record<string, { title: string; cls: string }> = {
  "booking canceled": { title: "Booking canceled", cls: "bg-red-50 text-red-900 border-red-200 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900" },
  "booking request": { title: "Booking request", cls: "bg-blue-50 text-blue-900 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200 dark:border-blue-900" },
  "booking confirmed": { title: "Booking confirmed", cls: "bg-green-50 text-green-900 border-green-200 dark:bg-green-950/30 dark:text-green-200 dark:border-green-900" },
};
const NOTICE_DEFAULT = {
  title: "Update",
  cls: "bg-neutral-50 text-neutral-900 border-neutral-200 dark:bg-neutral-800/50 dark:text-neutral-100 dark:border-neutral-700",
};

const SystemNotice = ({ msg }: { msg: Message }) => {
  const text = msg.body?.name || "";
  const n = (msg.event_message && NOTICE[msg.event_message]) || NOTICE_DEFAULT;
  let title = n.title;
  if (msg.event_message === "booking canceled") {
    const t = text.toLowerCase();
    title = t.includes("you cancelled") ? "You canceled this booking" : t.includes("host") ? "The host canceled this booking" : n.title;
  }
  return (
    <div className="flex justify-center py-1">
      <div className={`w-full max-w-md rounded-xl border px-4 py-2.5 text-center ${n.cls}`}>
        <p className="text-xs font-semibold uppercase tracking-wide">{title}</p>
        {text && <p className="mt-0.5 text-sm leading-snug">{text}</p>}
        <p className="mt-1 text-xs opacity-70">{formatTime(msg.created_at)}</p>
      </div>
    </div>
  );
};

const Avatar = ({ sender }: { sender: Sender }) =>
  sender?.image ? (
    <NcImage src={sender.image} alt="" className="h-8 w-8 rounded-full object-cover" />
  ) : (
    <div aria-hidden="true" className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 dark:bg-[#3A1F17] text-xs font-semibold text-orange-900 dark:text-[#FFB4A0]">
      {(sender?.name || "?").charAt(0).toUpperCase()}
    </div>
  );

const MessageThread: React.FC<Props> = ({ modifiedBookingMap, loadMoreMessages, showMore, invoiceCard }) => {
  const responses = Array.isArray(modifiedBookingMap) ? modifiedBookingMap : [modifiedBookingMap];
  const { currentUser } = useSelector((state: RootState) => state.user);
  const [loadingMore, setLoadingMore] = useState(false);

  const myId = currentUser?.id;
  const isHost = currentUser?.is_host;
  const isStudent = currentUser?.is_student;

  const visibility = (m: Message) => {
    const mine = myId === m.sender_id;
    const isSystem = (isHost && m.event_for === "host") || (isStudent && m.event_for === "student");
    return { show: m.event_for === "both" || mine || isSystem, isSystem };
  };

  // Only the newest invoice notice gets the live card (older ones were replaced).
  const lastInvoice: { id: number | null } = { id: null };
  responses.forEach((resp) =>
    (resp?.message || []).forEach((g) =>
      g.messages.forEach((m) => {
        if (visibility(m).isSystem && isInvoiceNotice(m)) lastInvoice.id = m.id;
      })
    )
  );

  const nextPage = responses[0]?.meta?.next_page;
  const total = responses.reduce(
    (acc, r) => acc + (r?.message || []).reduce((a, g) => a + g.messages.length, 0),
    0
  );

  const loadMore = async () => {
    if (!nextPage) return;
    setLoadingMore(true);
    try {
      await loadMoreMessages(nextPage);
    } finally {
      setLoadingMore(false);
    }
  };

  const card = "rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5 sm:p-6";

  if (showMore) {
    return (
      <section className={card} aria-busy="true" aria-label="Loading messages">
        <div className="space-y-4 animate-pulse">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`flex items-end gap-2 ${i % 2 ? "flex-row-reverse" : ""}`}>
              <div className="h-8 w-8 rounded-full bg-neutral-200 dark:bg-neutral-800" />
              <div className={`h-14 rounded-2xl bg-neutral-200 dark:bg-neutral-800 ${i % 2 ? "w-44" : "w-56"}`} />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (total === 0) {
    return (
      <section className={`${card} py-12 text-center`}>
        <p className="text-base font-medium text-neutral-900 dark:text-white">No messages yet</p>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">Start the conversation by sending a message above.</p>
      </section>
    );
  }

  return (
    <section className={card} aria-label="Conversation">
      {nextPage && (
        <div className="mb-4 flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="inline-flex min-h-[44px] items-center rounded-full border border-neutral-300 dark:border-neutral-600 px-5 text-sm font-medium text-neutral-900 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
          >
            {loadingMore ? "Loading..." : "Load earlier messages"}
          </button>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {responses.map((resp, ri) =>
          (resp?.message || []).map((group, gi) => (
            <div key={`g-${ri}-${gi}`} className="flex flex-col gap-3">
              <div className="flex justify-center py-1">
                <span className="rounded-full bg-neutral-100 dark:bg-neutral-800 px-3 py-1 text-xs font-medium text-neutral-600 dark:text-neutral-300">
                  {group.date}
                </span>
              </div>

              {group.messages.map((msg, mi) => {
                const { show, isSystem } = visibility(msg);
                if (!show) return null;
                if (isSystem) {
                  return (
                    <React.Fragment key={`m-${mi}`}>
                      <SystemNotice msg={msg} />
                      {invoiceCard && msg.id === lastInvoice.id && invoiceCard}
                    </React.Fragment>
                  );
                }

                const mine = myId === msg.sender_id;
                return (
                  <div key={`m-${mi}`} className={`flex items-end gap-2 ${mine ? "flex-row-reverse" : ""}`}>
                    {!mine && <div className="mb-5 shrink-0"><Avatar sender={msg.sender} /></div>}
                    <div className={`flex max-w-[80%] flex-col gap-1 ${mine ? "items-end" : "items-start"}`}>
                      {!mine && (
                        <span className="ml-1 text-xs font-medium text-neutral-600 dark:text-neutral-400">
                          {msg.sender?.name || "User"}
                        </span>
                      )}
                      <div
                        className={`rounded-2xl px-4 py-2.5 ${
                          mine
                            ? "rounded-br-md bg-[#C2410C] text-white"
                            : "rounded-bl-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                        }`}
                      >
                        {msg.body?.url ? (
                          <a
                            href={msg.body.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-sm font-medium underline ${mine ? "text-white" : "text-[#C2410C] dark:text-[#FF8A6E]"}`}
                          >
                            {msg.body?.name || "Attachment"}
                          </a>
                        ) : (
                          <>
                            {msg.body?.name && (
                              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{msg.body.name}</p>
                            )}
                            {msg.attachments?.length > 0 && (
                              <div className={`flex flex-wrap gap-1.5 ${msg.body?.name ? "mt-2" : ""}`}>
                                {msg.attachments.map((a: any, i: number) => (
                                  <a
                                    key={i}
                                    href={a.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`max-w-[200px] truncate rounded-lg px-2.5 py-1.5 text-xs font-medium underline ${
                                      mine ? "bg-white/20 text-white" : "bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100"
                                    }`}
                                  >
                                    {a.name}
                                  </a>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <span className="px-1 text-xs text-neutral-500 dark:text-neutral-400">{formatTime(msg.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default MessageThread;
